import { openDB } from 'idb';
import { ITopicDetail } from 'src/types/helpdesk/topic.type';

// Tên database và phiên bản cho IndexedDB
const DB_NAME = 'chat-files-db';
const DB_VERSION = 1;

/**
 * Hàm khởi tạo và kết nối tới IndexedDB
 * Tạo 2 object stores:
 * - 'files': Lưu trữ file attachments với key là roomId-fileId
 * - 'messages': Lưu trữ messages với key là roomId
 * @returns Promise<IDBPDatabase> - Database instance
 */
async function getDB() {
  return openDB(DB_NAME, DB_VERSION, {
    upgrade(db) {
      // Tạo object store cho files nếu chưa tồn tại
      if (!db.objectStoreNames.contains('files')) {
        db.createObjectStore('files', { keyPath: 'key' });
      }
      // Tạo object store cho messages nếu chưa tồn tại
      if (!db.objectStoreNames.contains('messages')) {
        db.createObjectStore('messages', { keyPath: 'roomId' });
      }
    }
  });
}

/**
 * Lưu file attachment vào IndexedDB với metadata đầy đủ
 * @param roomId - ID của phòng chat
 * @param fileId - ID duy nhất của file
 * @param file - Blob data của file
 * @param metadata - Object chứa thông tin file
 * @returns Promise<void>
 */
export async function saveFileWithMetadata(
  roomId: string,
  fileId: string,
  file: Blob,
  metadata: {
    fileName: string;
    fileType: string;
    fileSize: number;
    messageId?: string; // Link to message
  }
) {
  const db = await getDB();
  const key = `${roomId}-${fileId}`; // Tạo key duy nhất từ roomId và fileId
  await db.put('files', {
    key,
    roomId, // ← Thêm để query dễ hơn
    fileId,
    file,
    fileName: metadata.fileName,
    fileType: metadata.fileType,
    fileSize: metadata.fileSize,
    messageId: metadata.messageId,
    savedAt: Date.now(),
    accessCount: 0, // Track usage
    lastAccessAt: Date.now()
  });
}

/**
 * Lấy file attachment từ IndexedDB theo roomId và fileId
 * @param roomId - ID của phòng chat
 * @param fileId - ID của file cần lấy
 * @returns Promise<FileData | undefined>
 */
export async function getFileWithMetadata(roomId: string, fileId: string) {
  const db = await getDB();
  const key = `${roomId}-${fileId}`;
  const result = await db.get('files', key);

  if (result) {
    // ✅ Update access tracking
    result.accessCount = (result.accessCount || 0) + 1;
    result.lastAccessAt = Date.now();
    await db.put('files', result);
  }

  return result;
}

/**
 * Lưu danh sách messages của một phòng chat vào IndexedDB
 * Sử dụng để cache messages offline hoặc backup
 * @param roomId - ID của phòng chat
 * @param messages - Mảng các message objects
 * @returns Promise<void>
 */
export async function saveMessagesToDB(roomId: string, messages: ITopicDetail[]) {
  const db = await getDB();
  await db.put('messages', { roomId, messages, savedAt: Date.now() });
}

/**
 * Lấy danh sách messages đã lưu từ IndexedDB theo roomId
 * @param roomId - ID của phòng chat
 * @returns Promise<any[] | null> - Mảng messages hoặc null nếu không tìm thấy
 */
export async function getMessagesFromDB(roomId: string) {
  try {
    const db = await openDB(DB_NAME, DB_VERSION);
    const tx = db.transaction('messages', 'readonly');
    const store = tx.objectStore('messages');
    const result = await store.get(roomId);

    await tx.done;

    return result || null;
  } catch (error) {
    console.error('❌ Failed to get messages from IndexedDB:', error);
    return null;
  }
}

/**
 * Xóa toàn bộ dữ liệu chat từ IndexedDB
 * Bao gồm: files và messages của tất cả các phòng chat
 * Sử dụng khi logout hoặc cần reset cache
 * @returns Promise<void>
 */
export async function clearAllChatDB() {
  const db = await getDB();
  await db.clear('files'); // Xóa tất cả files
  await db.clear('messages'); // Xóa tất cả messages
}

/**
 * Xóa tất cả files của một phòng chat cụ thể
 * @param roomId - ID của phòng chat cần xóa files
 * @returns Promise<void>
 */
export async function clearRoomFiles(roomId: string) {
  const db = await getDB();
  const tx = db.transaction('files', 'readwrite');
  const store = tx.objectStore('files');

  // Lấy tất cả keys và filter theo roomId
  const allKeys = await store.getAllKeys();
  const roomKeys = allKeys.filter((key) => typeof key === 'string' && key.startsWith(`${roomId}-`));

  // Xóa từng file của room
  for (const key of roomKeys) {
    await store.delete(key);
  }

  await tx.done;
}

/**
 * Xóa messages của một phòng chat cụ thể
 * @param roomId - ID của phòng chat cần xóa messages
 * @returns Promise<void>
 */
export async function clearRoomMessages(roomId: string) {
  const db = await getDB();
  await db.delete('messages', roomId);
}

/**
 * Lấy tổng kích thước files đã lưu trong database
 * @returns Promise<number> - Tổng kích thước tính bằng bytes
 */
export async function getTotalStorageSize(): Promise<number> {
  const db = await getDB();
  const files = await db.getAll('files');

  let totalSize = 0;
  for (const fileData of files) {
    if (fileData.file && fileData.file.size) {
      totalSize += fileData.file.size;
    }
  }

  return totalSize;
}

/**
 * Kiểm tra xem một file đã tồn tại trong database chưa
 * @param roomId - ID của phòng chat
 * @param fileId - ID của file
 * @returns Promise<boolean> - true nếu file đã tồn tại
 */
export async function fileExists(roomId: string, fileId: string): Promise<boolean> {
  const db = await getDB();
  const key = `${roomId}-${fileId}`;
  const result = await db.get('files', key);
  return !!result;
}

/**
 * Lấy danh sách tất cả files của một phòng chat
 * @param roomId - ID của phòng chat
 * @returns Promise<Array<{key: string, file: Blob, type: string, savedAt: number}>>
 */
export async function getRoomFiles(roomId: string) {
  const db = await getDB();
  const allFiles = await db.getAll('files');

  return allFiles.filter((fileData) => fileData.key.startsWith(`${roomId}-`));
}

/**
 * Xóa các file và messages cũ hơn một khoảng thời gian nhất định
 * @param maxAgeMs - Thời gian tối đa tính bằng milliseconds, mặc định là 30 ngày
 * @returns Promise<void>
 */
export async function cleanupOldData(maxAgeMs: number = 30 * 24 * 60 * 60 * 1000) {
  const db = await getDB();
  const cutoffTime = Date.now() - maxAgeMs;

  try {
    const tx = db.transaction(['files', 'messages'], 'readwrite');

    // Cleanup old files
    const filesStore = tx.objectStore('files');
    const allFiles = await filesStore.getAll();
    let filesRemoved = 0;

    for (const file of allFiles) {
      if (file.savedAt && file.savedAt < cutoffTime) {
        await filesStore.delete(file.key);
        filesRemoved++;
      }
    }

    // Cleanup old messages (optional)
    const messagesStore = tx.objectStore('messages');
    const allMessages = await messagesStore.getAll();
    let messagesRemoved = 0;

    for (const msg of allMessages) {
      if (msg.savedAt && msg.savedAt < cutoffTime) {
        await messagesStore.delete(msg.roomId);
        messagesRemoved++;
      }
    }

    await tx.done;

    console.log('🧹 Cleanup completed:', {
      filesRemoved,
      messagesRemoved
    });
  } catch (error) {
    console.error('❌ Cleanup failed:', error);
  }
}

/**
 * Lấy thống kê về số lượng và kích thước của files và messages trong database
 * @returns Promise<{ filesCount: number, messagesCount: number, totalFilesSize: number, totalMessagesSize: number, totalSize: number, formattedTotalSize: string }>
 */
export async function getStorageStats() {
  const db = await getDB();

  try {
    const [files, messages] = await Promise.all([db.getAll('files'), db.getAll('messages')]);

    const filesSize = files.reduce((total, f) => {
      return total + (f.file?.size || 0);
    }, 0);

    const messagesSize = messages.reduce((total, m) => {
      return total + JSON.stringify(m.messages || []).length;
    }, 0);

    return {
      filesCount: files.length,
      messagesCount: messages.length,
      totalFilesSize: filesSize,
      totalMessagesSize: messagesSize,
      totalSize: filesSize + messagesSize,
      formattedTotalSize: formatBytes(filesSize + messagesSize)
    };
  } catch (error) {
    console.error('❌ Failed to get storage stats:', error);
    return {
      filesCount: 0,
      messagesCount: 0,
      totalFilesSize: 0,
      totalMessagesSize: 0,
      totalSize: 0,
      formattedTotalSize: '0 B'
    };
  }
}

/**
 * Get all topic IDs that have messages in IndexedDB
 */
export async function getAllTopicIdsFromDB(): Promise<string[]> {
  try {
    const db = await getDB();
    const allMessages = await db.getAll('messages');

    // Extract unique room/topic IDs
    const topicIds = [...new Set(allMessages.map((msg) => msg.roomId || msg.topicId))].filter(Boolean);

    console.log('📋 Found topic IDs in IndexedDB:', topicIds.length);
    return topicIds as string[];
  } catch (error) {
    console.error('❌ Failed to get topic IDs from IndexedDB:', error);
    return [];
  }
}

/**
 * Get all files from IndexedDB with blob URLs
 */
export async function getAllFilesFromDB(): Promise<
  Array<{
    key: string;
    blob: Blob;
    metadata: {
      fileName?: string;
      fileSize?: number;
      fileType?: string;
    };
    lastAccessed: number;
  }>
> {
  try {
    const db = await getDB();
    const allFiles = await db.getAll('files');

    console.log('📁 Found files in IndexedDB:', allFiles.length);

    return allFiles.map((file) => ({
      key: `${file.roomId}-${file.fileId}`, // Create consistent key format
      blob: file.file, // The actual blob data
      metadata: {
        fileName: file.fileName,
        fileSize: file.fileSize,
        fileType: file.fileType
      },
      lastAccessed: file.lastAccessAt || Date.now()
    }));
  } catch (error) {
    console.error('❌ Failed to get files from IndexedDB:', error);
    return [];
  }
}

// #region Helper Functions
function formatBytes(bytes: number, decimals = 2) {
  if (bytes === 0) return '0 B';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['B', 'KB', 'MB', 'GB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}
// #endregion
